//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputAdornment, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'
import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  toCommaDecString,
  weiToNumber,
} from '@shared/conversions'

import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

import { AddressPill } from '@react/components/addresses/AddressPill'
import { useOtDispatch, useOtSelector } from '@react/redux/store'
import { repayDynamicWithdrawTx } from '@react/signedTxs/repayDynamicWithdrawTx'
import { logOT } from '@shared/loggingShared'
import { DynamicWithdrawalActivityCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import { TableHeaderItem } from '@react/components/TableHeaderItem'

export default function AdminDynamicRepayRedeemForm(props: {
  activity: DynamicWithdrawalActivityCTO
  symbol: string
  liquidityTokenSymbol: string
}): ReactElement {
  const { activity, symbol, liquidityTokenSymbol } = props
  const { poolAddr, lender, assets, shares } = activity

  const dispatch = useOtDispatch()
  const [amount, setAmount] = React.useState<string>(erc20WeiToErc20DecString(assets))
  const [fees, setFees] = React.useState<string>(erc20WeiToErc20DecString(0))
  const [errorAmount, setErrorAmount] = React.useState<string | undefined>(undefined)
  const [errorFees, setErrorFees] = React.useState<string | undefined>(undefined)
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const errorCheck = (amountBN: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', amountBN)

    if (amountBN === 0n) {
      setErrorAmount('Amount can not be zero')
    } else {
      setErrorAmount(undefined)
    }
  }

  const handleOnFocusAmount = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', amount)
    const ppt = commaDecStringToBigNumberWei(amount)
    setAmount(commaDecStringToErcDecString(amount))

    errorCheck(ppt)
  }
  const handleOnBlurAmount = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', amount)
    setAmount(toCommaDecString(amount))
  }
  const handleOnChangeAmount = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setAmount(event.target.value)
    try {
      const ppt = textFieldValueToBigNumberWei(event)

      errorCheck(ppt)
    } catch (e) {
      logOT.error(e)
    }
  }

  const errorCheckFees = (feesBN: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', feesBN)

    setErrorFees(undefined)
  }
  const handleOnFocusFees = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', amount)
    const ppt = commaDecStringToBigNumberWei(fees)
    setFees(commaDecStringToErcDecString(fees))

    errorCheckFees(ppt)
  }
  const handleOnBlurFees = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', fees)
    setFees(toCommaDecString(fees))
  }
  const handleOnChangeFees = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setFees(event.target.value)
    try {
      const ppt = textFieldValueToBigNumberWei(event)

      errorCheckFees(ppt)
    } catch (e) {
      logOT.error(e)
    }
  }

  const repayEarlyWithdrawAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      repayDynamicWithdrawTx({
        activity,
        amount: commaDecStringToErc20Wei(amount),
        fees: commaDecStringToErc20Wei(fees),
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = weiToNumber(poolCTO?.totalAssets) < Number(amount)

  console.log('poolCTO?.totalAssets', weiToNumber(poolCTO?.totalAssets), Number(amount), disableButton)
  return (
    <div data-testid="REPAY_EARLY_REDEEM_FORM">
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Please set the Early Withdrawal Amount for the Eathly Withdrawal Request.
          </Typography>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            This is the amount of {poolCTO.liquidityTokenSymbol} the Lender will receive for this Early Withdrawal
            Request. It should be equal to the Net Sale Proceeds of the Loan Collateral liquidated.
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            Total Early Withdraw accepted principal {activity.assets} in {poolCTO.liquidityTokenSymbol} Wei{' '}
          </Typography>
        </Grid>
      </Grid>

      {activity && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TableHeaderItem>Lender</TableHeaderItem>
            </Grid>
            <Grid item xs={4}>
              <TableHeaderItem>Shares</TableHeaderItem>
            </Grid>
            <Grid item xs={4}>
              <TableHeaderItem>Asset Value({liquidityTokenSymbol})</TableHeaderItem>
            </Grid>
          </Grid>

          <Grid container sx={{ marginBottom: 4 }}>
            <Grid item xs={4}>
              <Box sx={{ width: '75%' }}>
                <AddressPill addr={lender} />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
                {erc20WeiToCommaDecString(shares)} {symbol}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
                {erc20WeiToCommaDecString(assets)} {liquidityTokenSymbol}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container sx={{ marginTop: 2 }}>
        <Grid item xs={12}>
          <InputLabel htmlFor="usdc">Repayment Withdrawal Amount</InputLabel>
          <TextField
            id="usdc"
            InputProps={{
              endAdornment: <InputAdornment position="start">{poolCTO.liquidityTokenSymbol}</InputAdornment>,
            }}
            inputProps={{
              'data-testid': 'usdc',
            }}
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Withdraw Amount"
            value={amount}
            type={'tel'}
            onFocus={handleOnFocusAmount}
            onChange={handleOnChangeAmount}
            onBlur={handleOnBlurAmount}
            error={!!errorAmount}
            helperText={errorAmount}
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor="usdc">Fees</InputLabel>
          <TextField
            id="usdc"
            InputProps={{
              endAdornment: <InputAdornment position="start">{poolCTO.liquidityTokenSymbol}</InputAdornment>,
            }}
            inputProps={{
              'data-testid': 'usdc',
            }}
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Withdraw Fees"
            value={fees}
            type={'tel'}
            onFocus={handleOnFocusFees}
            onChange={handleOnChangeFees}
            onBlur={handleOnBlurFees}
            error={!!errorFees}
            helperText={errorFees}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Button
            disabled={isSubmitted || disableButton || Number(amount) <= 0.00001}
            sx={sx}
            onClick={() => {
              repayEarlyWithdrawAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Repay the Withdraw Request</Typography>
          </Button>
        </Grid>
        {disableButton && (
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
            available in your vault ({weiToNumber(poolCTO?.totalAssets)}). Please deposit funds to the borrowerVault
          </Typography>
        )}
      </Grid>
    </div>
  )
}
